class Conversation extends EventHandler {

  constructor(data = {}) {
    super();
    Utils.autoFeed(this, {
      "id"           : null,
      "user_group_id": null,
      "status"       : "open",
      "created_at"   : null,
      "firstMessageDate": null,
      "ephemeral"    : false,
      "analysis_summary": null
    }, data);

    this.messages = [];

    Utils.forEach(data.messages, message => {
      this._addMessageReference(new Message(message));
    });

    if (!this.created_at && this.firstMessageDate) {
      this.created_at = this.firstMessageDate;
    }

    this._object_created_at = this.created_at 
      ? (typeof this.created_at === 'string' ? new Date(this.created_at) : this.created_at)
      : new Date();

    // Life span
    this.life_span                  = Conversation.LIFE_SPAN.ONLINE;
    this.stored_promise_resolve     = null;
    this.stored_promise_reject      = null;
    this.stored_promise_name        = null;
    this._life_span_initialized     = false;
    this._submit_to                 = 'publish';

    // Notification
    this.greeting_message           = null;
    this.unread_messages            = 0;

    // Ask email if not answering
    this.conversation_answered      = !!data.answered_at;
    this.waiting_for_answer_timeout = null;

    // Typing
    this.is_typing                  = false;
    this.user_typings               = {};

    // Message queue
    this.message_queue              = null;
    this.upload_resolves            = {};

    // Survey
    this.current_survey             = 0;

    // Bot Tree
    this.forced_bot_tree            = null;
    this._bot_tree_running          = data.bot_tree_running;

    // Message input
    this._force_hide_message_input          = false
  }

  /* GETTERS */

  get kind() {
    if (this.user_group_id) return this.team.kind;
    else return "one";
  }

  get team() {
    return Teams.findOrDefault(this.user_group_id);
  }

  get last_activity() {
    let lastMessage = this.getLastMessage(true);
    return lastMessage
            ? lastMessage.created_at
            : this._object_created_at;
  }

  get lifeSpanInitialized() {
    return this._life_span_initialized;
  }

  /* ID */

  setId(conversationId) {
    this.id = conversationId;
    this.messages.forEach(message => {
      message.conv_id = conversationId;
    });
  }

  existsOnServer() {
    return this.id != null;
  }

  /* MESSAGES */

  getLastMessage(excludeNotifications) {
    if (!excludeNotifications) {
      return this.messages.length > 0 ? this.messages[this.messages.length - 1] : null;
    }
    else {
      for (let i = this.messages.length - 1 ; i >= 0 ; i--) {
        if (!["notification", "typing"].includes(this.messages[i].type)) return this.messages[i];
      }
      return this.getLastMessage();
    }
    return null;
  }

  getMessage(messageId) {
    return this.messages.filter(msg => msg.id == messageId)[0];
  }

  getMessageByHash(hash) {
    return hash
            ? this.messages.filter(msg => msg.hash == hash)[0]
            : null;
  }

  addMessage(message, silent = false) {
    if (message.constructor !== Message) throw "The parameter must be initialized as a Message object";
    this._addMessageReference(message);
    this._trigger('new_message', message, silent);
    if (message.fromVisitor())
      this._trigger('new_visitor_message', message, silent);
  }

  removeMessage(messageOrId) {
    let message = messageOrId.constructor == Message ? messageOrId : this.getMessage(messageOrId);
    let ind = this.messages.indexOf(message);
    if (ind == -1) return null;
    this.resetConversationEventsFor(message);
    this._trigger('remove_message', message);
    message.conversation = null;
    this.messages.splice(ind, 1);
    message.constructor.dereference(message.id);
    return message;
  }

  _addMessageReference(message) {
    message.conversation = this;
    if (typeof message.parser == "undefined") {
      message.parser = Message.PARSER.MARKDOWN;
    }
    this.initConversationEventsFor(message);
    this.messages.push(message);
    message.constructor.reference(message);
  }

  reorderMessage(message, index) {
    let oldIndex = this.messages.indexOf(message);
    if (oldIndex == -1) return null;
    this.messages.splice(oldIndex, 1);
    this.messages.splice(index, 0, message);
    this._trigger('reorder_message', message);
  }

  getMessageIndex(message) {
    return this.messages.indexOf(message);
  }

  /**
   * Tries to identify the message that contains the Contact question,
   * usually located in the first messages excluding "Hello" messages
   * @returns {Message}
   */
  lookForQuestion() {
    const check = message => message.text.length > 50;
    let longestMessage = null;
    let messagesFoundCount = 0;
    for (let i = 0; i < this.messages.length; i++) {
      const message = this.messages[i];
      if (message.fromGuest()) {
        messagesFoundCount++;
        if (check(message)) return message;
        if (!longestMessage || message.text.length > longestMessage.text.length) {
          longestMessage = message;
        }
        if (messagesFoundCount > 5) break;
      }
    }
    return longestMessage;
  }

  /* CONVERSAITON EVENTS */

  initConversationEventsFor(message) {
    this.resetConversationEventsFor(message);
    if (message.keep_at_bottom) {
      message.conversationEvents.push(this.on('new_message', newMessage => {
        if (!newMessage.keep_at_bottom) {
          this.reorderMessage(message, this.messages.length);
        }
      }));
    }
  }

  resetConversationEventsFor(message) {
    Utils.forEach(message.conversationEvents, conversationEvent => {
      this.off(conversationEvent.event, conversationEvent.handler);
    });
  }

  /* BOOLEANS */

  isOpened() { return this.isOpen.apply(this, arguments); }
  isOpen() {
    return this.kind == "many"
            || this.status == "open"
            || (this.status == "unresolved" && Conversations.xeno);
  }

  isSnoozed() {
    return this.status == "snoozed"
  }

  isLive() {
    return this.isOpened()
            && (!Conversations.xeno
                  || new Date() - new Date(this.last_activity) < Utils.time.h(2));
  }

  hasMandatoryButtons() {
    for (let i = this.messages.length - 1 ; i >= 0 ; i--) {
      if (this.messages[i].mandatory_buttons || this.messages[i].hasMandatoryAttachment()) return true;
      else if (this.messages[i].fromVisitor()) return false;
    }
    return false;
  }

  hasBotTreeInput() {
    let lastMessage = this.getLastMessage();
    let result = false;
    if (lastMessage) {
      Utils.forEach(lastMessage.attachments, attachment => {
        if (attachment.type == "Attachment::Input" && !attachment.options.talking_to_human) {
          result = true;
        }
      });
    }
    return result;
  }

  hasVisitorMessage() {
    for (let i = this.messages.length - 1 ; i >= 0 ; i--) {
      if (this.messages[i].fromVisitor()) return true;
    }
    return false;
  }

  shouldDisplayInput() {
    return this.isOpened() && !this.hasBotTreeInput() && !this.hasMandatoryButtons() && !this._force_hide_message_input;
  }

  /* LIFE SPAN */

  initLifeSpan() {
    this._life_span_initialized = true;
    this._trigger('life-span-init');
  }

  storePromise(resolve, reject, name) {
    this.stored_promise_resolve = resolve;
    this.stored_promise_reject = reject;
    this.stored_promise_name = name;
  }

  resolvePromise(param, name) {
    this._resolveRejectPromise(param, name, false);
  }

  rejectPromise(param, name) {
    this._resolveRejectPromise(param, name, true);
  }

  _resolveRejectPromise(param, name, reject) {
    if (name === null || name == this.stored_promise_name) {
      if (reject) {
        if (this.stored_promise_reject) this.stored_promise_reject(param);
      }
      else {
        if (this.stored_promise_resolve) this.stored_promise_resolve(param);
      }
      this.stored_promise_resolve = null;
      this.stored_promise_reject = null;
      this.stored_promise_name = null;
    }
  }

  submitTo(where) {
    this._submit_to = where;
    this._trigger('submit_to_changed', where);
  }

  get submit_to() {
    return this._submit_to;
  }

  /* BOT TREE */

  get bot_tree_running() {
    return this._bot_tree_running;
  }

  set bot_tree_running(bot_tree_running) {
    this._bot_tree_running = bot_tree_running;
    this._trigger('bot_tree_running_changed', bot_tree_running);
  }

  /* TYPING */

  addUserTyping(typingData) {
    let currentlyTypingData = this.user_typings[typingData.user_id];

    if (!currentlyTypingData) {
      this.user_typings[typingData.user_id] = currentlyTypingData = typingData;
      this._trigger('user_typing', typingData);
    }

    if (currentlyTypingData.stopTimeout) {
      clearTimeout(currentlyTypingData.stopTimeout);
      delete currentlyTypingData.stopTimeout;
    }

    currentlyTypingData.stopTimeout = this._setTimeout(() => {
      this.removeUserTyping(typingData);
    }, 6000);
  }

  removeUserTyping(typingData) {
    let currentlyTypingData = this.user_typings[typingData.user_id];

    if (currentlyTypingData) {
      if (!typingData.at_message_sent) delete this.user_typings[typingData.user_id];
      currentlyTypingData.at_message_sent = typingData.at_message_sent;
      clearTimeout(currentlyTypingData.stopTimeout);
      delete currentlyTypingData.stopTimeout;
      this._trigger('user_stopped_typing', currentlyTypingData);
    }
  }

  /* UNREAD MESSAGES */

  markAllMessagedAsRead() {
    for (var i = 0; i < this.messages.length; i++) {
      if (!this.messages[i].fromVisitor()) {
        if (this.messages[i].status <= 2) {
          this.messages[i].status = Message.STATUS.READ
        } else if (this.messages[i].status == Message.STATUS.DELIVERED_BY_EMAIL) {
          this.messages[i].status = Message.STATUS.READ_BY_EMAIL
        }
      }
    }
  }

  unreadMessages() {
    return this.messages.filter(message => {
      const fromVisitor = message.fromVisitor()
      const isNotification = message.type == 'notification'
      const read = !!~[
        Message.STATUS.READ,
        Message.STATUS.READ_BY_EMAIL
      ].indexOf(message.status)
      return !fromVisitor && !isNotification && !read
    })
  }

  /* QUEUE */

  /**
   * Prevents sending two messages to the conversation "null"
   * Returns a promise that resolves when the promise of the handler is resolved
   * @param  {Function} handler  Function must return a Promise that resolves once we got a conversation id from the server
   */
  async addToSendQueue(handler) {
    if (this.id == null) {
      // First message
      if (this.message_queue == null) {
        this.message_queue = [];
        await handler();
        Utils.forEach(this.message_queue, queuedHandler => {
          queuedHandler();
        });
        this.message_queue = null;
      }
      // Queued messages
      else {
        await new Promise((resolve, reject) => {
          this.message_queue.push(async () => {
            await handler();
            resolve();
          });
        });
      }
    }
    else handler();
  }

  /* MISC */

  setWaitingForAnswerTimeout(handler, time) {
    this.waiting_for_answer_timeout = this._setTimeout(handler, time);
  }

  resetWaitingTimeout() {
    if (this.waiting_for_answer_timeout) {
      this._clearTimeout(this.waiting_for_answer_timeout);
      this.waiting_for_answer_timeout = null;
    }
  }

  /* DEBUG */

  print() {
    let msgList = "";
    this.messages.forEach(msg => {
      msgList += '[' + (new Date(msg.created_at)).toUTCString().match(/\d+:\d+:\d+/)[0] + '] ' + msg.name + ': ' + msg.plain_text + "\n";
    });
    Utils.log('Messages from conversation ' + this.id, msgList);
  }

  destroy() {
    this._trigger('destroy');
  }

}

Conversation.LIFE_SPAN = {
  OFFLINE: 0,
  ONLINE: 1
};
